// queryKey.js — THE canonical key of a Desk search (B1214 step 2, owner 2026-09-05).
//
// Every cache layer — memory, IndexedDB, the server's result cache — keys on the string this file
// produces, and NOTHING ELSE may derive one. The owner's rule 12: "cache keys MUST come from the
// canonical filter definition… If two semantically different searches can accidentally share a key,
// cached results become incorrect rather than merely stale." So the filter half of the key is not a
// JSON.stringify of a state object (whose key order, empty strings and defaults are accidents of the
// UI) — it is the registry's own wire serialisation, `wireParams()` from deskFilters.js: the exact
// list of (wire key, encoded value) pairs the server would receive, sorted. Two filter states that
// send the same request get the same key; two that send different requests never share one.
//
// What else changes semantics and is therefore in the key (rule 12's list): the POOL (Live /
// Archive / all — `status` on the book scopes, `leadStatus` on leads), the SCOPE (all / mine /
// leads and the lenses that ride it: claimed/worked), the VIEWER whose scope it is (agent id — the
// same request answers differently for two agents on `mine`), the SORT, the PAGE (offset / limit /
// unknown offset), the PERIOD, and a VERSION that bumps whenever filter semantics or the cached
// payload shape change (a stale key must miss, never serve yesterday's meaning).
//
// Not in the key: anything that does not change the answer — thumbs=0, range_facets (a response
// flag: keyed separately by the caller that needs it), the request id.
import { DESK_FILTERS } from './deskFilters.js'
import { applySearchFilterParams } from './searchWire.js'

/** Bump when filter SEMANTICS or the cached payload SHAPE change (rule 12).
 *  q2 (F-104, 2026-09-23): the filter half is now the FULL wire; every q1 key is abandoned, never reused. */
export const QUERY_KEY_VERSION = 'q2'

const enc = (v) => (v == null || v === '' ? '' : String(v))
// A SET's members are unordered (OR within the facet): ✓Vake ✓Saburtalo and ✓Saburtalo ✓Vake are one
// request. The registry encodes the array in the order the UI holds it, so the key sorts the CSV of
// every set-kind wire. Caught by the first test written for this file.
const SET_WIRES = new Set(DESK_FILTERS.filter(f => f.kind === 'set').flatMap(f => Array.isArray(f.wire) ? f.wire : [f.wire]).filter(Boolean))
const canon = (k, v) => (SET_WIRES.has(k) ? String(v).split(',').filter(Boolean).sort().join(',') : enc(v))

/**
 * @param {object} q
 * @param {string}  q.scope        'all' | 'mine' | 'leads' | …
 * @param {string}  [q.pool]       'active' | 'archived' | '' (book scopes); the leads lifecycle goes in leadStatus
 * @param {string}  [q.leadStatus]
 * @param {string}  [q.viewer]     agent id the scope is resolved for
 * @param {object}  [q.filters]    the committed filter state (registry keys)
 * @param {string}  [q.sort]
 * @param {string}  [q.period]
 * @param {number}  [q.offset]
 * @param {number}  [q.limit]
 * @param {number}  [q.unknownOffset]
 * @param {boolean} [q.claimed]    the Mine lens on leads
 * @param {boolean} [q.worked]
 * @returns {string} a stable, human-readable key
 */
export function queryKey(q) {
  // 🔴 F-104 (2026-09-23): THE FULL WIRE, not the registry half. This line used to read
  // `wireParams(q.filters, q.scope)`, which is only the registry rows — deal, kind, rooms/bedrooms,
  // text, near, move-in and the legacy date trio are sent by hand-written lines the registry cannot
  // express yet, so a land search and an apartment search produced ONE key. The page-0 paint showed
  // the other search's cached cards, and load-more served its pre-warmed page without a request.
  // applySearchFilterParams IS what getAgentListings sends, so "same request ⇔ same key" now holds
  // by construction. `sort` stays out of the filter half — it is keyed in the head below.
  const pairs = []
  if (q.filters) {
    const wire = new URLSearchParams()
    const { sort, ...filters } = q.filters
    applySearchFilterParams(wire, filters, { scope: q.scope, unknownOffset: q.unknownOffset })
    for (const [k, v] of wire) pairs.push([k, v])
  }
  // sorted by wire key then value: the registry iterates in declaration order, which is stable today
  // and an accident tomorrow — the key must not change because someone reorders a row.
  const filt = pairs.map(([k, v]) => `${k}=${canon(k, v)}`).sort().join('&')
  const head = [
    QUERY_KEY_VERSION,
    `scope:${enc(q.scope) || 'all'}`,
    `pool:${enc(q.pool)}`,
    `lead:${enc(q.leadStatus)}`,
    `viewer:${enc(q.viewer)}`,
    `lens:${q.claimed ? 'c' : ''}${q.worked ? 'w' : ''}`,
    `sort:${enc(q.sort)}`,
    `period:${enc(q.period)}`,
    `page:${enc(q.offset) || 0}/${enc(q.limit) || ''}/${enc(q.unknownOffset) || 0}`,
  ].join('|')
  return `${head}|${filt}`
}

/** The key with the page component removed — what groups every page of one search. */
export function searchKey(q) {
  return queryKey({ ...q, offset: 0, limit: '', unknownOffset: 0 })
}
